import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Badge, Button } from '../../components/ui';
import { AccordionItem } from '../../components/ui/Accordion';
import { ChevronLeftIcon, PencilIcon } from '../../components/ui/Icons';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { ReportExportButtons } from '../../components/reports/ReportExportButtons';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import {
  generateMgaAiOverview,
  getMgaAiOverview,
  getMgaAiOverviewHistory,
  type MgaAiOverviewResponse,
} from '../../services/mutations';
import {
  formatMgaStatus,
  moduleConfig,
  statusBadgeVariant,
} from './config';
import { exportMgaPeriodExcel, exportMgaPeriodPdf } from './exportPeriod';
import { MGA_EXCEL_PARITY, parityBadgeLabel, type ParityStatus } from './parityMap';
import {
  useMgaAuditEvents,
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaCrewRequests,
  useMgaPeriod,
  useMgaRemittance,
} from './useData';
import {
  exportMgaAllotmentRemittance,
  exportMgaBondedByCrew,
  exportMgaBulkPayslipsPdf,
  exportMgaCashBookStatement,
  exportMgaClosePackPdf,
  remittanceRowsFromRequests,
} from './reports';

function formatAmt(n: number | null | undefined, currency = 'USD') {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(Number(n));
}

const formatGeneratedAt = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const readinessVariant = (readiness: MgaAiOverviewResponse['overview']['readiness']) => {
  switch (readiness) {
    case 'READY':
      return 'success' as const;
    case 'CONDITIONAL':
      return 'warning' as const;
    case 'NOT_READY':
      return 'danger' as const;
    default:
      return 'secondary' as const;
  }
};

type MetricTone = 'default' | 'danger' | 'warning' | 'ok';

type TopMetric = {
  id: string;
  label: string;
  value: string;
  hint?: string;
  tone?: MetricTone;
};

const toneValueClass = (tone: MetricTone, isDark: boolean): string => {
  if (tone === 'danger') return isDark ? 'text-red-400' : 'text-red-600';
  if (tone === 'warning') return isDark ? 'text-amber-400' : 'text-amber-600';
  if (tone === 'ok') return isDark ? 'text-emerald-400' : 'text-emerald-700';
  return '';
};

const MetricCell: React.FC<Omit<TopMetric, 'id'>> = ({ label, value, hint, tone = 'default' }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="min-w-0 px-3 py-3 sm:px-4">
      <p className={cn('text-xs font-semibold uppercase tracking-wide leading-snug', colors.textMuted)}>
        {label}
      </p>
      <p
        className={cn(
          'mt-1.5 text-sm font-semibold tabular-nums truncate',
          tone === 'default' ? colors.text : toneValueClass(tone, isDark),
        )}
        title={value}
      >
        {value}
      </p>
      {hint ? (
        <p className={cn('mt-0.5 text-xs truncate', colors.textMuted)} title={hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const MetricsGroup: React.FC<{
  title: string;
  metrics: TopMetric[];
  columnsClass?: string;
}> = ({ title, metrics, columnsClass = 'grid-cols-2 md:grid-cols-4' }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border shadow-sm overflow-hidden', colors.surface, colors.border)}>
      <div className={cn('border-b px-4 py-2', colors.border, colors.headerBg)}>
        <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>{title}</h3>
      </div>
      <div className={cn('grid divide-x divide-y xl:divide-y-0', columnsClass, colors.border)}>
        {metrics.map(({ id, label, value, hint, tone }) => (
          <MetricCell key={id} label={label} value={value} hint={hint} tone={tone} />
        ))}
      </div>
    </section>
  );
};

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('overflow-hidden rounded-xl border shadow-sm', colors.surface, colors.border)}>
      <div
        className={cn(
          'flex items-start justify-between gap-3 border-b px-5 py-3',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="min-w-0">
          <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
          {subtitle && <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
};

const AiOverviewBody: React.FC<{ data: MgaAiOverviewResponse; compactMeta?: boolean }> = ({
  data,
  compactMeta = false,
}) => {
  const { colors } = useTheme();
  const { overview } = data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="min-w-0">
          <p className={cn('text-sm font-semibold', colors.text)}>{overview.headline}</p>
          <p className={cn('mt-1.5 text-sm leading-relaxed', colors.textSecondary)}>{overview.summary}</p>
        </div>
        <Badge variant={readinessVariant(overview.readiness)}>{overview.readiness.replace(/_/g, ' ')}</Badge>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {(
          [
            ['Strengths', overview.strengths],
            ['Risks', overview.risks],
            ['Recommendations', overview.recommendations],
          ] as const
        ).map(([title, items]) => (
          <div key={title}>
            <p className={cn('text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}>{title}</p>
            {items.length ? (
              <ul className={cn('space-y-1.5 text-sm list-disc pl-4', colors.text)}>
                {items.map((itemText) => (
                  <li key={itemText}>{itemText}</li>
                ))}
              </ul>
            ) : (
              <p className={cn('text-sm', colors.textMuted)}>—</p>
            )}
          </div>
        ))}
      </div>
      {!compactMeta ? (
        <p className={cn('text-xs', colors.textMuted)}>
          Generated {formatGeneratedAt(data.generatedAt)} · {data.model}
        </p>
      ) : null}
    </div>
  );
};

function parityTone(status: ParityStatus, isDark: boolean) {
  if (status === 'ok') {
    return isDark ? 'bg-emerald-950/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800';
  }
  if (status === 'partial') {
    return isDark ? 'bg-amber-950/40 text-amber-200' : 'bg-amber-100 text-amber-900';
  }
  return isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600';
}

const Dashboard: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const { showError, showSuccess } = useNotification();
  const { id } = useParams<{ id: string }>();
  const periodId = id ? parseInt(id, 10) : NaN;
  const { item, isLoading } = useMgaPeriod(Number.isInteger(periodId) ? periodId : 0);
  const { data: cash } = useMgaCashEntries(Number.isInteger(periodId) ? periodId : 0);
  const { data: bonded } = useMgaBondedLines(Number.isInteger(periodId) ? periodId : 0);
  const { data: requests } = useMgaCrewRequests(Number.isInteger(periodId) ? periodId : 0);
  const { data: auditEvents } = useMgaAuditEvents(Number.isInteger(periodId) ? periodId : 0);
  const { data: remittance } = useMgaRemittance(Number.isInteger(periodId) ? periodId : 0);
  const canEdit =
    can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_mga', 'edit');
  const [parityOpen, setParityOpen] = useState(false);

  const [aiOverview, setAiOverview] = useState<MgaAiOverviewResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHydrating, setAiHydrating] = useState(Number.isInteger(periodId));
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<MgaAiOverviewResponse[]>([]);
  const [historySelectedId, setHistorySelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!Number.isInteger(periodId) || periodId <= 0) {
      setAiOverview(null);
      setAiHydrating(false);
      return;
    }
    let cancelled = false;
    setAiOverview(null);
    setAiHydrating(true);
    getMgaAiOverview(periodId)
      .then((saved) => {
        if (!cancelled) {
          setAiOverview(saved);
          if (saved) setAiPanelOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setAiOverview(null);
      })
      .finally(() => {
        if (!cancelled) setAiHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodId]);

  const summary = useMemo(() => {
    const lines = item?.lines ?? [];
    const ccy = item?.currency || 'USD';
    const cashBal = cash.reduce((s, e) => {
      const amt = Number(e.amountPeriod ?? e.amount) || 0;
      return e.entryType === 'EXPENSE' ? s - amt : s + amt;
    }, 0);
    const cashIn = cash
      .filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const cashOut = cash
      .filter((e) => e.entryType === 'EXPENSE')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const ctm = cash
      .filter((e) => e.entryType === 'CTM')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const bondedIn = bonded
      .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const bondedOut = bonded
      .filter((l) => l.section === 'EXPENSE')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const cashByMaster = requests
      .filter((r) => r.requestType === 'CASH_BY_MASTER')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const allotments = requests
      .filter((r) => r.requestType === 'ALLOTMENT')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const missingWageSplit = lines.filter((l) => l.missingWageSplit).length;
    return {
      lines,
      ccy,
      cashBal,
      cashIn,
      cashOut,
      ctm,
      bondedIn,
      bondedOut,
      bondedBal: bondedIn - bondedOut,
      cashByMaster,
      allotments,
      cashReqCount: requests.filter((r) => r.requestType === 'CASH_BY_MASTER').length,
      allotReqCount: requests.filter((r) => r.requestType === 'ALLOTMENT').length,
      missingWageSplit,
    };
  }, [item, cash, bonded, requests]);

  const glanceMetrics = useMemo((): TopMetric[] => {
    if (!item) return [];
    return [
      {
        id: 'crew',
        label: 'Crew',
        value: String(summary.lines.length),
        hint: 'Payroll lines',
      },
      {
        id: 'net',
        label: 'Net due',
        value: formatAmt(item.totals?.netDue, summary.ccy),
        hint: `Gross ${formatAmt(item.totals?.grossPayable, summary.ccy)}`,
        tone: Number(item.totals?.netDue) < 0 ? 'danger' : 'default',
      },
      {
        id: 'ot',
        label: 'OT total',
        value: formatAmt(item.totals?.otTotal, summary.ccy),
      },
      {
        id: 'cash',
        label: 'Cash on hand',
        value: formatAmt(summary.cashBal, summary.ccy),
        hint: `CTM ${formatAmt(summary.ctm, summary.ccy)}`,
        tone: summary.cashBal < 0 ? 'warning' : 'default',
      },
      {
        id: 'bonded',
        label: 'Bonded balance',
        value: formatAmt(summary.bondedBal, summary.ccy),
        hint: `${bonded.length} lines`,
      },
      {
        id: 'allot',
        label: 'Allotments',
        value: formatAmt(summary.allotments, summary.ccy),
        hint: `${summary.allotReqCount} requests`,
      },
      {
        id: 'cashReq',
        label: 'Cash by master',
        value: formatAmt(summary.cashByMaster, summary.ccy),
        hint: `${summary.cashReqCount} requests`,
      },
      {
        id: 'wageGaps',
        label: 'Wage split gaps',
        value: String(summary.missingWageSplit),
        hint: summary.missingWageSplit ? 'Needs attention' : 'All set',
        tone: summary.missingWageSplit > 0 ? 'danger' : 'ok',
      },
    ];
  }, [item, summary, bonded.length]);

  const metricFor = (key: (typeof MGA_EXCEL_PARITY)[number]['metricKey']) => {
    if (!key || !item) return null;
    switch (key) {
      case 'gross':
        return formatAmt(item.totals?.grossPayable, summary.ccy);
      case 'net':
        return formatAmt(item.totals?.netDue, summary.ccy);
      case 'ot':
        return formatAmt(item.totals?.otTotal, summary.ccy);
      case 'cashBal':
        return formatAmt(summary.cashBal, summary.ccy);
      case 'ctm':
        return formatAmt(summary.ctm, summary.ccy);
      case 'bondedBal':
        return formatAmt(summary.bondedBal, summary.ccy);
      case 'cashByMaster':
        return formatAmt(summary.cashByMaster, summary.ccy);
      case 'allotments':
        return formatAmt(summary.allotments, summary.ccy);
      case 'payslips':
        return `${summary.lines.length} crew`;
      default:
        return null;
    }
  };

  const exportPayload = useMemo(
    () => (item ? { period: item, cash, bonded, requests } : null),
    [item, cash, bonded, requests],
  );

  const handleGenerateAiOverview = async () => {
    if (!Number.isInteger(periodId) || periodId <= 0) return;
    setAiLoading(true);
    try {
      const result = await generateMgaAiOverview(periodId);
      setAiOverview(result);
      setAiPanelOpen(true);
      if (historyOpen) {
        setHistoryItems((prev) => [result, ...prev.filter((row) => row.id !== result.id)]);
        setHistorySelectedId(result.id);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not generate AI overview.');
    } finally {
      setAiLoading(false);
    }
  };

  const openHistoryModal = async () => {
    if (!Number.isInteger(periodId) || periodId <= 0) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const rows = await getMgaAiOverviewHistory(periodId);
      setHistoryItems(rows);
      setHistorySelectedId(rows[0]?.id ?? aiOverview?.id ?? null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not load AI overview history.');
      setHistoryItems([]);
      setHistorySelectedId(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setHistoryOpen(false);
    setHistoryLoading(false);
  };

  const selectedHistory = historyItems.find((row) => row.id === historySelectedId) ?? null;

  if (!Number.isInteger(periodId)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className={cn(colors.primaryFg, 'underline')}>
          Back to MGA
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>MGA period not found</h1>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('mt-4 inline-block text-sm underline', colors.primaryFg)}
        >
          Back to MGA
        </Link>
      </div>
    );
  }

  const sheet = moduleConfig.module.route.sheet(item.id);
  const vesselName = item.vessel?.vesselName ?? `Vessel #${item.vesselId}`;

  const ledgerRows: Array<{ label: string; value: string; hint?: string }> = [
    { label: 'Payroll gross', value: formatAmt(item.totals?.grossPayable, summary.ccy) },
    { label: 'OT total', value: formatAmt(item.totals?.otTotal, summary.ccy) },
    {
      label: 'Payroll net due',
      value: formatAmt(item.totals?.netDue, summary.ccy),
      hint: `${summary.lines.length} crew`,
    },
    { label: 'CTM remittances', value: formatAmt(summary.ctm, summary.ccy) },
    { label: 'Cash received', value: formatAmt(summary.cashIn, summary.ccy) },
    { label: 'Cash paid out', value: formatAmt(summary.cashOut, summary.ccy) },
    { label: 'Cash on hand', value: formatAmt(summary.cashBal, summary.ccy) },
    { label: 'Bonded incoming', value: formatAmt(summary.bondedIn, summary.ccy) },
    { label: 'Bonded expense', value: formatAmt(summary.bondedOut, summary.ccy) },
    { label: 'Bonded balance', value: formatAmt(summary.bondedBal, summary.ccy) },
    {
      label: 'Cash by master req.',
      value: formatAmt(summary.cashByMaster, summary.ccy),
      hint: `${summary.cashReqCount} rows`,
    },
    {
      label: 'Allotment req.',
      value: formatAmt(summary.allotments, summary.ccy),
      hint: `${summary.allotReqCount} rows`,
    },
  ];

  const sheets = [
    { title: 'Payroll', tab: 'payroll', count: summary.lines.length, desc: 'Wages, OT, advances, deductions' },
    { title: 'Cash / CTM', tab: 'cash', count: cash.length, desc: 'Income, CTM, crew cash expenses' },
    { title: 'Bonded', tab: 'bonded', count: bonded.length, desc: 'Incoming stock and crew issues' },
    { title: 'Crew requests', tab: 'requests', count: requests.length, desc: 'Cash by master and allotments' },
  ];

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="w-full space-y-4 px-4 py-6 sm:px-6 lg:px-8 pb-8">
        <Link
          to={moduleConfig.module.route.list}
          className={cn(
            'inline-flex items-center gap-1 text-sm font-medium',
            colors.textSecondary,
            'hover:underline',
          )}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          MGA
        </Link>

        {/* Identity */}
        <section className={cn('overflow-hidden rounded-xl border shadow-sm', colors.surface, colors.border)}>
          <div className="flex flex-col gap-5 px-5 py-6 sm:flex-row sm:items-start sm:px-6">
            <div
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ring-1 ring-inset',
                colors.surfaceHover,
                colors.text,
                colors.border,
              )}
              aria-hidden
            >
              MGA
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h1 className={cn('truncate text-xl font-semibold sm:text-2xl', colors.text)}>
                    {vesselName}
                  </h1>
                  <p className={cn('mt-1 text-sm', colors.textMuted)}>
                    {[
                      formatDateGlobal(item.periodFrom) + ' → ' + formatDateGlobal(item.periodTo),
                      item.currency,
                      item.vessel?.vesselAbrv,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {exportPayload && (
                    <ReportExportButtons
                      onExportExcel={async () => {
                        try {
                          await exportMgaPeriodExcel(exportPayload);
                          showSuccess('Excel exported.');
                        } catch {
                          showError('Excel export failed.');
                        }
                      }}
                      onExportPDF={async () => {
                        try {
                          await exportMgaPeriodPdf(exportPayload);
                          showSuccess('PDF exported.');
                        } catch {
                          showError('PDF export failed.');
                        }
                      }}
                    />
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => setParityOpen((v) => !v)}>
                    {parityOpen ? 'Hide parity' : 'Excel parity'}
                  </Button>
                  {canEdit && (
                    <Link to={sheet}>
                      <Button type="button" variant="accent">
                        <PencilIcon className="h-4 w-4" />
                        Edit sheet
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={statusBadgeVariant(item.status)}>{formatMgaStatus(item.status)}</Badge>
                <span
                  className={cn(
                    'inline-flex rounded-md px-2 py-0.5 font-mono text-xs',
                    colors.surfaceHover,
                    colors.textSecondary,
                  )}
                >
                  #{item.id}
                </span>
                {summary.missingWageSplit > 0 ? (
                  <Badge variant="danger">{summary.missingWageSplit} wage split gap</Badge>
                ) : null}
                {item.accDocument ? <Badge variant="info">Journal linked</Badge> : null}
              </div>

              {item.notes ? <p className={cn('text-sm', colors.textMuted)}>{item.notes}</p> : null}
              {item.status === 'CLOSED' && item.masterApprovedByName ? (
                <p className={cn('text-xs', colors.textMuted)}>
                  Closed by master approval: {item.masterApprovedByName}
                  {item.masterApprovedAt ? ` · ${formatDateGlobal(item.masterApprovedAt)}` : ''}
                </p>
              ) : null}
            </div>
          </div>
        </section>

        {/* AI overview */}
        <AccordionItem
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn('text-sm font-semibold', colors.text)}>AI overview</span>
              {aiOverview ? (
                <Badge variant={readinessVariant(aiOverview.overview.readiness)}>
                  {aiOverview.overview.readiness.replace(/_/g, ' ')}
                </Badge>
              ) : null}
            </span>
          }
          description="Close readiness summary from payroll, cash, bonded, and requests"
          open={aiPanelOpen}
          onToggle={setAiPanelOpen}
          headerAction={
            <div className="flex flex-wrap items-center gap-2 pr-1">
              <Button type="button" variant="outline" size="sm" onClick={openHistoryModal}>
                History
              </Button>
              <Button
                type="button"
                variant={aiOverview ? 'outline' : 'accent'}
                size="sm"
                loading={aiLoading}
                onClick={handleGenerateAiOverview}
              >
                {aiOverview ? 'Refresh' : 'Generate'}
              </Button>
            </div>
          }
          className={cn(colors.surface, 'shadow-sm rounded-xl')}
        >
          {aiHydrating && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading saved overview…</p>
          ) : aiLoading && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Analyzing MGA period…</p>
          ) : aiOverview ? (
            <AiOverviewBody data={aiOverview} />
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>
              Generate an AI overview from period totals, cash book, bonded stock, allotments, and wage-split flags.
            </p>
          )}
        </AccordionItem>

        {parityOpen && (
          <Panel
            title="Excel parity (CAPE BYRON draft)"
            subtitle="Side-by-side map of workbook sheets → Atlas MGA, with live totals"
          >
            <ul className="space-y-3">
              {MGA_EXCEL_PARITY.map((row) => {
                const metric = metricFor(row.metricKey);
                return (
                  <li
                    key={`${row.excelSheet}-${row.atlas}`}
                    className={cn(
                      'grid gap-2 border-b border-dashed pb-3 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto]',
                      colors.borderLight,
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('text-sm font-medium', colors.text)}>{row.excelSheet}</span>
                        <span className={cn('text-xs', colors.textMuted)}>→ {row.atlas}</span>
                        <span
                          className={cn(
                            'rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                            parityTone(row.status, isDark),
                          )}
                        >
                          {parityBadgeLabel(row.status)}
                        </span>
                      </div>
                      <p className={cn('mt-1 text-xs', colors.textMuted)}>{row.note}</p>
                    </div>
                    {metric != null && (
                      <div className={cn('text-sm font-medium tabular-nums sm:text-right', colors.text)}>
                        {metric}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Panel>
        )}

        <MetricsGroup
          title="At a glance"
          metrics={glanceMetrics}
          columnsClass="grid-cols-2 md:grid-cols-4 xl:grid-cols-8"
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Account summary" subtitle="Totals across this period">
            <dl className="space-y-0">
              {ledgerRows.map((row) => (
                <div
                  key={row.label}
                  className={cn(
                    'grid grid-cols-[1fr_auto] gap-x-3 border-b border-dashed py-2 last:border-0',
                    colors.borderLight,
                  )}
                >
                  <dt className={cn('text-sm', colors.textSecondary)}>
                    {row.label}
                    {row.hint ? (
                      <span className={cn('ml-1.5 text-xs', colors.textMuted)}>({row.hint})</span>
                    ) : null}
                  </dt>
                  <dd className={cn('text-sm font-medium tabular-nums', colors.text)}>{row.value}</dd>
                </div>
              ))}
            </dl>
            <div className={cn('mt-4 flex items-end justify-between border-t pt-4', colors.border)}>
              <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                Balance due to crew
              </span>
              <span className={cn('text-xl font-semibold tabular-nums', colors.text)}>
                {formatAmt(item.totals?.netDue, summary.ccy)}
              </span>
            </div>
          </Panel>

          <Panel title="Workbook" subtitle="Open a sheet to view or edit">
            <ul className="space-y-1">
              {sheets.map((s) => (
                <li key={s.tab}>
                  <Link
                    to={`${sheet}?tab=${s.tab}`}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-3 transition-colors',
                      colors.interactiveHover,
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('text-sm font-semibold', colors.text)}>{s.title}</span>
                        <span
                          className={cn(
                            'rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                            colors.surfaceHover,
                            colors.textSecondary,
                          )}
                        >
                          {s.count}
                        </span>
                      </div>
                      <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{s.desc}</p>
                    </div>
                    <span className={cn('text-sm', colors.primaryFg)}>Open →</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <Panel title="Reports" subtitle="Download Excel or PDF for office / owners / bank">
          {!exportPayload ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading…</p>
          ) : (
            <ul className="space-y-2">
              {(
                [
                  {
                    key: 'close',
                    label: 'Master’s close pack',
                    hint: 'Summary, payroll totals, cash, bonded, signature block (PDF)',
                    run: async () => {
                      await exportMgaClosePackPdf(exportPayload);
                      showSuccess('Close pack PDF exported.');
                    },
                  },
                  {
                    key: 'payslips',
                    label: 'Bulk payslips',
                    hint: 'All crew payslips in one PDF',
                    run: async () => {
                      await exportMgaBulkPayslipsPdf(exportPayload);
                      showSuccess('Payslips PDF exported.');
                    },
                  },
                  {
                    key: 'allot',
                    label: 'Allotment remittance',
                    hint: 'Crew / allottee / bank / amount (Excel)',
                    run: async () => {
                      const rows =
                        remittance.length > 0
                          ? remittance
                          : remittanceRowsFromRequests(requests, item.lines ?? []);
                      await exportMgaAllotmentRemittance(item, rows, 'excel');
                      showSuccess('Allotment remittance exported.');
                    },
                  },
                  {
                    key: 'allot-csv',
                    label: 'Allotment bank file (CSV)',
                    hint: 'Fixed columns for bank upload: beneficiary, bank, account, amount, currency, value date, ref',
                    run: async () => {
                      const rows =
                        remittance.length > 0
                          ? remittance
                          : remittanceRowsFromRequests(requests, item.lines ?? []);
                      await exportMgaAllotmentRemittance(item, rows, 'csv');
                      showSuccess('Allotment bank CSV exported.');
                    },
                  },
                  {
                    key: 'cash',
                    label: 'Cash book statement',
                    hint: 'Running balance for the month (PDF)',
                    run: async () => {
                      await exportMgaCashBookStatement(exportPayload, 'pdf');
                      showSuccess('Cash book PDF exported.');
                    },
                  },
                  {
                    key: 'bonded',
                    label: 'Bonded by crew',
                    hint: 'Issues grouped by crew (Excel)',
                    run: async () => {
                      await exportMgaBondedByCrew(exportPayload, 'excel');
                      showSuccess('Bonded-by-crew exported.');
                    },
                  },
                ] as const
              ).map((r) => (
                <li
                  key={r.key}
                  className={cn(
                    'flex flex-wrap items-center justify-between gap-2 border-b border-dashed py-2.5 last:border-0',
                    colors.borderLight,
                  )}
                >
                  <div className="min-w-0">
                    <p className={cn('text-sm font-medium', colors.text)}>{r.label}</p>
                    <p className={cn('text-xs', colors.textMuted)}>{r.hint}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await r.run();
                      } catch (e) {
                        showError(e instanceof Error ? e.message : 'Export failed.');
                      }
                    }}
                  >
                    Export
                  </Button>
                </li>
              ))}
              <li className={cn('pt-1 text-xs', colors.textMuted)}>
                <Link to="/mga/settings/posting" className={cn('underline', colors.primaryFg)}>
                  Posting settings
                </Link>
              </li>
            </ul>
          )}
        </Panel>

        <div className="grid gap-4 lg:grid-cols-2">
          <Panel
            title="Finance journal"
            subtitle="Created when the period is closed (if posting is configured)"
          >
            {item.accDocument ? (
              <div className="space-y-2">
                <p className={cn('text-sm', colors.text)}>
                  Document{' '}
                  <Link
                    to={`/accounting/journals/${item.accDocument.id}`}
                    className={cn('font-medium underline', colors.primaryFg)}
                  >
                    {item.accDocument.code || `#${item.accDocument.id}`}
                  </Link>
                </p>
                <p className={cn('text-xs', colors.textMuted)}>
                  Status {item.accDocument.status}
                  {item.accDocument.postingDate
                    ? ` · ${formatDateGlobal(item.accDocument.postingDate)}`
                    : ''}
                </p>
                {item.accDocument.description ? (
                  <p className={cn('text-xs', colors.textSecondary)}>{item.accDocument.description}</p>
                ) : null}
              </div>
            ) : item.status === 'CLOSED' ? (
              <p className={cn('text-sm', colors.textMuted)}>
                No AccDocument linked. Configure mga_posting_config (wages expense + crew payable) to
                post on close.
              </p>
            ) : (
              <p className={cn('text-sm', colors.textMuted)}>
                Close the period to create a draft AccDocument journal when posting is enabled.
              </p>
            )}
          </Panel>

          <Panel title="Change history" subtitle="Recent edits on this period">
            {auditEvents.length === 0 ? (
              <p className={cn('text-sm', colors.textMuted)}>No audit events yet.</p>
            ) : (
              <ul className="max-h-72 space-y-0 overflow-y-auto">
                {auditEvents.slice(0, 40).map((ev) => (
                  <li
                    key={ev.id}
                    className={cn('border-b border-dashed py-2 last:border-0', colors.borderLight)}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className={cn('text-sm', colors.text)}>{ev.summary}</p>
                      <span className={cn('text-xs tabular-nums', colors.textMuted)}>
                        {formatDateGlobal(ev.createdAt)}
                      </span>
                    </div>
                    <p className={cn('mt-0.5 text-[11px]', colors.textMuted)}>
                      {ev.action} · {ev.entityType}
                      {ev.userName ? ` · ${ev.userName}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      <Modal isOpen={historyOpen} onClose={closeHistoryModal} size="2xl">
        <ModalHeader>
          <ModalTitle>AI overview history</ModalTitle>
        </ModalHeader>
        <ModalContent className={colors.headerBg}>
          {historyLoading ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading history…</p>
          ) : historyItems.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No saved overviews yet.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-[minmax(12rem,16rem)_1fr] min-h-[20rem]">
              <div className={cn('rounded-lg border overflow-hidden', colors.border, colors.surface)}>
                <ul className={cn('max-h-[28rem] overflow-y-auto divide-y', colors.divide)}>
                  {historyItems.map((row) => {
                    const active = row.id === historySelectedId;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={() => setHistorySelectedId(row.id)}
                          className={cn(
                            'w-full text-left px-3 py-2.5 transition-colors',
                            active ? colors.surfaceHover : colors.interactiveHover,
                          )}
                        >
                          <p className={cn('text-xs font-medium', colors.textMuted)}>
                            {formatGeneratedAt(row.generatedAt)}
                          </p>
                          <p className={cn('mt-0.5 text-sm font-medium truncate', colors.text)}>
                            {row.overview.headline || 'Overview'}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant={readinessVariant(row.overview.readiness)}>
                              {row.overview.readiness.replace(/_/g, ' ')}
                            </Badge>
                            {aiOverview?.id === row.id ? <Badge variant="info">Latest</Badge> : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className={cn('rounded-lg border p-4', colors.border, colors.surface)}>
                {selectedHistory ? (
                  <div className="space-y-3">
                    <p className={cn('text-xs', colors.textMuted)}>
                      {formatGeneratedAt(selectedHistory.generatedAt)} · {selectedHistory.model}
                    </p>
                    <AiOverviewBody data={selectedHistory} compactMeta />
                  </div>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>Select a generation to view.</p>
                )}
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={closeHistoryModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Dashboard;
